import { FormattedMessage, useIntl } from "react-intl";
import {
  ActionBar,
  ActionBarButtons,
  CreateActionButton,
  url,
  Loader,
  useApi,
  useFlipperQuery,
  Clue,
  ButtonGroup,
  List,
  Container,
  Row,
  Column,
} from "lcm-iot-commons";
import isElementHiddenViaFlipper from "lcm-iot-commons/client/lib/utils/flipperUtils";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { connectSubscriptionShape } from "../../shapes/connectSubscriptionsShape";
import { extractConnectSubscriptions } from "../../extractors/connectSubscriptionsExtractor";
import ConnectSubscriptionItem from "./ConnectSubscriptionItem";
import { IConnectSubscriptionsResponse } from "../../shapes/subscriptionsResponseShape";

export function ConnectSubscriptions() {
  const api = useApi();
  const { data: flipper } = useFlipperQuery();
  const intl = useIntl();

  const [totalCount, setTotalCount] = useState<number>(0);
  const [subscriptions, setSubscriptions] = useState<
    Array<connectSubscriptionShape>
  >([]);

  const parameters = {
    status: "open,confirmed,payment_required,non_renewing",
  };
  const { data, isLoading } = api.get.useQuery(
    "/api_subscriptions",
    parameters,
  );

  useEffect(() => {
    if (!data) return;

    const freeOrPaid = (connectSubscription: connectSubscriptionShape) =>
      connectSubscription.externalReference ||
      connectSubscription.status === "open"
        ? "paid"
        : "free";

    const prepareSubscriptions = () => {
      const connectSubscriptions = extractConnectSubscriptions(
        data as IConnectSubscriptionsResponse,
      );

      return connectSubscriptions.map(
        (connectSubscription: connectSubscriptionShape) => ({
          ...connectSubscription,
          pricingModel: freeOrPaid(connectSubscription),
        }),
      );
    };

    const subscriptionsCollection = prepareSubscriptions();
    setSubscriptions(subscriptionsCollection);
    setTotalCount(subscriptionsCollection.length);
  }, [data]);

  const renderItems = () =>
    subscriptions?.map((connectSubscription: connectSubscriptionShape) => (
      <ConnectSubscriptionItem
        subscription={connectSubscription}
        key={connectSubscription.id}
      />
    ));

  const actionBarButtons = (
    <ActionBarButtons>
      {!isElementHiddenViaFlipper(
        flipper ?? {},
        "hideConnectSubscriptionsCreateAction",
      ) && (
        <CreateActionButton
          // @ts-expect-error TS(2322)
          id="create-subscription-button"
          disabled={isLoading}
          target="/plan_variants"
        />
      )}
    </ActionBarButtons>
  );

  const badgeCount = totalCount > 0 ? totalCount : null;
  const badge = isLoading ? (
    <span className="loading">
      <span>.</span>
      <span>.</span>
      <span>.</span>
    </span>
  ) : (
    badgeCount
  );

  const noSubscriptionConnectFound =
    !isLoading && totalCount === 0 ? (
      // @ts-expect-error TS(2322) FIXME: Type '{ children: Element; id: string; title: stri... Remove this comment to see the full error message
      <Clue
        id="no-subscription-found"
        title={intl.formatMessage({ id: "subscription.no_connect_found" })}
        details={intl.formatMessage({
          id: "subscription.no_connect_found.ask_create",
        })}
        className="notification"
        highlighted
      >
        <ButtonGroup>
          <Link className="btn btn-primary" to={url("/plan_variants")}>
            {intl.formatMessage({ id: "button.create_subscription" })}
          </Link>
        </ButtonGroup>
      </Clue>
    ) : null;

  return (
    <Container>
      <Row>
        <Column>
          <ActionBar>
            <h1 id="subscriptions-header">
              <FormattedMessage id="label.connect_subscriptions" />
              <span
                id="subscriptions-count"
                data-testid="subscriptions-count"
                className="badge"
              >
                {badge}
              </span>
            </h1>
            {actionBarButtons}
          </ActionBar>

          {noSubscriptionConnectFound}
          {subscriptions?.length > 0 && (
            <List id="subscription-list">{renderItems()}</List>
          )}
          <Loader id="subscriptions-list-loader" loading={isLoading} />
        </Column>
      </Row>
    </Container>
  );
}

export default ConnectSubscriptions;
